import type { JewelsResponse } from '@kenocyber/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchJewels,
  HuntApiError,
  incorporatePrimordialHeart,
  insertJewel,
  removeJewel,
  swapJewel,
} from '../api/hunt';

/**
 * O estado das joias, tal como o servidor o tem (gate V28C).
 *
 * ## Nada de otimista, e a razão é uma só
 *
 * A view inteira vem em cada resposta, e é ela que fica. Nenhum socket é pintado
 * cheio antes de o servidor responder, nenhuma peça é movida na lista à espera
 * de confirmação. Um socket que se pinta e depois volta atrás é pior do que um
 * socket que esperou 200 ms - e uma recusa deixa o ecrã exatamente como estava.
 *
 * ## Uma intenção, uma chave
 *
 * A chave de idempotência é gerada quando a intenção nasce e é REPETIDA se a
 * mesma intenção for repetida: um duplo clique, um retry após uma rede que caiu.
 * O servidor devolve o mesmo resultado e não escreve segunda vez.
 *
 * ## Um conflito relê
 *
 * Quando o servidor recusa, o estado autoritativo é lido outra vez em vez de ser
 * adivinhado a partir do erro. Duas abas encaixarem no mesmo assento é um caso
 * normal, e a segunda tem de ver o que a primeira fez.
 */

export interface JewelsState {
  readonly view: JewelsResponse | null;
  readonly loading: boolean;
  readonly busy: boolean;
  readonly error: string | null;
}

/**
 * O que uma mutação diz a quem a pediu.
 *
 * A view já foi adotada no estado; isto é a outra metade da resposta - se
 * correu, e com que NOME o servidor recusou quando não correu. Um modal que
 * tenha de ficar aberto e escrever a razão precisa do nome, e um `boolean` não
 * distingue "esta nave está anunciada" de "este coração já foi gasto".
 */
export type JewelRunOutcome = { ok: true } | { ok: false; reason: string | null };

/** A intenção pendente, para a chave sobreviver a um segundo clique. */
interface PendingIntent {
  readonly signature: string;
  readonly key: string;
}

function newKey(label: string): string {
  const random = Math.random().toString(36).slice(2, 10);
  return `jewel-${label}-${Date.now().toString(36)}-${random}`;
}

export function useJewels(options: { enabled?: boolean } = {}) {
  const enabled = options.enabled ?? true;
  const [state, setState] = useState<JewelsState>({
    view: null,
    loading: false,
    busy: false,
    error: null,
  });
  const pending = useRef<PendingIntent | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!enabled) {
      return;
    }
    setState((current) => ({ ...current, loading: true, error: null }));
    try {
      const view = await fetchJewels();
      if (alive.current) {
        setState({ view, loading: false, busy: false, error: null });
      }
    } catch {
      if (alive.current) {
        setState((current) => ({
          ...current,
          loading: false,
          error: 'Não foi possível ler as suas joias.',
        }));
      }
    }
  }, [enabled]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Corre uma mutação e adota a view que voltar.
   *
   * A assinatura da intenção é o que decide se uma chave é reutilizada: o mesmo
   * gesto sobre o mesmo assento com a mesma peça é a MESMA intenção, e um
   * segundo clique não pode virar uma segunda escrita.
   */
  const run = useCallback(
    async (
      signature: string,
      call: (idempotencyKey: string) => Promise<JewelsResponse | { owned: never[] }>,
    ): Promise<JewelRunOutcome> => {
      const reuse = pending.current?.signature === signature ? pending.current.key : null;
      const key = reuse ?? newKey(signature.slice(0, 12));
      pending.current = { signature, key };
      setState((current) => ({ ...current, busy: true, error: null }));
      try {
        const response = (await call(key)) as JewelsResponse;
        if (alive.current) {
          /* A view INTEIRA que o servidor devolveu. Nunca um remendo local. */
          setState({
            view: {
              contractVersion: 1,
              owned: response.owned,
              skins: response.skins,
              /* Copiado campo a campo, como os outros: um spread deixaria um campo
               * futuro do servidor chegar à interface sem ninguém decidir. */
              ships: response.ships,
            },
            loading: false,
            busy: false,
            error: null,
          });
        }
        pending.current = null;
        return { ok: true };
      } catch (error) {
        /*
         * Uma recusa relê em vez de adivinhar.
         *
         * O erro diz o que falhou; não diz como está a base agora. Outra aba
         * pode ter ocupado o assento no meio, e o que o jogador tem de ver é o
         * estado real - não o estado anterior com uma mensagem por cima.
         */
        if (alive.current) {
          setState((current) => ({
            ...current,
            busy: false,
            error: 'O servidor recusou a operação.',
          }));
        }
        await load();
        /*
         * A chave SOBREVIVE à recusa, e é isso que torna um retry seguro.
         *
         * Se a resposta se perdeu depois de o servidor ter escrito, repetir a
         * MESMA intenção repete a MESMA chave: o envelope idempotente devolve o
         * mesmo resultado e não escreve segunda vez. Deitá-la fora aqui é que
         * seria a maneira de incorporar dois corações por causa de uma rede.
         */
        return {
          ok: false,
          reason: error instanceof HuntApiError ? error.reason : null,
        };
      }
    },
    [load],
  );

  const insert = useCallback(
    (skinInstanceId: string, socketIndex: number, jewelInstanceId: string) =>
      run(`insert:${skinInstanceId}:${socketIndex}:${jewelInstanceId}`, (idempotencyKey) =>
        insertJewel({ skinInstanceId, socketIndex, jewelInstanceId, idempotencyKey }),
      ),
    [run],
  );

  const remove = useCallback(
    (skinInstanceId: string, socketIndex: number) =>
      run(`remove:${skinInstanceId}:${socketIndex}`, (idempotencyKey) =>
        removeJewel({ skinInstanceId, socketIndex, idempotencyKey }),
      ),
    [run],
  );

  const swap = useCallback(
    (skinInstanceId: string, socketIndex: number, jewelInstanceId: string) =>
      run(`swap:${skinInstanceId}:${socketIndex}:${jewelInstanceId}`, (idempotencyKey) =>
        swapJewel({ skinInstanceId, socketIndex, jewelInstanceId, idempotencyKey }),
      ),
    [run],
  );

  /**
   * Incorpora um Coração numa nave (gate V28F.C).
   *
   * A assinatura é `(coração, nave)`, e é ela que define "a mesma intenção":
   * confirmar duas vezes a mesma incorporação repete a chave e o servidor
   * responde a mesma coisa sem escrever outra vez. Escolher OUTRA nave é outra
   * intenção, e tem chave nova - senão o envelope responderia
   * `idempotency_conflict` a um jogador que só mudou de ideias.
   */
  const incorporate = useCallback(
    (jewelInstanceId: string, shipInstanceId: string) =>
      run(`primordial:${jewelInstanceId}:${shipInstanceId}`, (idempotencyKey) =>
        incorporatePrimordialHeart({ shipInstanceId, jewelInstanceId, idempotencyKey }),
      ),
    [run],
  );

  return { state, load, insert, remove, swap, incorporate };
}
